import { AchievementItem, SkillCard } from '@/components/ui';
import { aboutData } from '@/data/about';
import React from 'react';

export interface PortfolioAboutProps {
    isMobile?: boolean;
}

export const PortfolioAbout: React.FC<PortfolioAboutProps> = ({ isMobile = false }) => {
    // Conditional styling based on mobile/desktop
    const containerClasses = isMobile ? 'bg-vscode-bg text-vscode-text p-6' : 'bg-vscode-bg text-vscode-text p-8';

    const maxWidthClasses = isMobile ? 'max-w-4xl mx-auto' : 'max-w-6xl mx-auto';

    const headerMarginClasses = isMobile ? 'text-center mb-10' : 'text-center mb-12';

    const titleClasses = isMobile
        ? 'text-3xl font-bold text-vscode-accent mb-4'
        : 'text-4xl font-bold text-vscode-accent mb-4';

    const subtitleClasses = isMobile
        ? 'text-vscode-text-muted text-base max-w-2xl mx-auto'
        : 'text-vscode-text-muted text-lg max-w-2xl mx-auto';

    const skillsSectionMarginClasses = isMobile ? 'mb-12' : 'mb-16';

    const skillsTitleClasses = isMobile
        ? 'text-2xl font-bold text-vscode-text text-center mb-6'
        : 'text-3xl font-bold text-vscode-text text-center mb-8';

    const skillsGridClasses = isMobile ? 'grid gap-6' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8';

    const achievementsTitleClasses = isMobile
        ? 'text-2xl font-bold text-vscode-text text-center mb-6'
        : 'text-3xl font-bold text-vscode-text text-center mb-8';

    const achievementsGridClasses = isMobile ? 'grid gap-6' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8';

    const achievementCardTitleClasses = isMobile
        ? 'text-lg font-semibold text-vscode-accent mb-4 text-center'
        : 'text-xl font-semibold text-vscode-accent mb-4 text-center';

    return (
        <div className={containerClasses}>
            <div className={maxWidthClasses}>
                {/* Header */}
                <div className={headerMarginClasses}>
                    <h1 className={titleClasses}>{aboutData.title}</h1>
                    <p className={subtitleClasses}>{aboutData.subtitle}</p>
                </div>

                {/* Special Skills Section */}
                <section className={skillsSectionMarginClasses}>
                    <h2 className={skillsTitleClasses}>Special Skills</h2>
                    <div className={skillsGridClasses}>
                        {aboutData.skills.map((skill, index) => (
                            <SkillCard key={index} skill={skill} className={!isMobile ? 'hover:shadow-lg' : ''} />
                        ))}
                    </div>
                </section>

                {/* Achievements Section */}
                <section>
                    <h2 className={achievementsTitleClasses}>Achievements</h2>
                    <div className={achievementsGridClasses}>
                        {/* Academic Results */}
                        <div className='bg-vscode-bg-secondary border border-gray-600/30 rounded-lg p-6'>
                            <h3 className={achievementCardTitleClasses}>🎓 Academic Results</h3>
                            <ul className='space-y-3'>
                                {aboutData.achievements.academic.map((achievement, index) => (
                                    <AchievementItem key={index}>{achievement.content}</AchievementItem>
                                ))}
                            </ul>
                        </div>

                        {/* University Awards */}
                        <div className='bg-vscode-bg-secondary border border-gray-600/30 rounded-lg p-6'>
                            <h3 className={achievementCardTitleClasses}>🏆 University Awards</h3>
                            <ul className='space-y-3'>
                                {aboutData.achievements.university.map((achievement, index) => (
                                    <AchievementItem key={index}>{achievement.content}</AchievementItem>
                                ))}
                            </ul>
                        </div>

                        {/* Software Downloads */}
                        <div className='bg-vscode-bg-secondary border border-gray-600/30 rounded-lg p-6'>
                            <h3 className={achievementCardTitleClasses}>📦 Software Impact</h3>
                            <ul className='space-y-3'>
                                {aboutData.achievements.software.map((achievement, index) => (
                                    <AchievementItem key={index}>{achievement}</AchievementItem>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};
